// E-Sign Via Derwent — drag-and-drop Field Editor.
// Broker uploads PDF, drags Signature/Initials/Date/Name/Text/Checkbox
// fields onto the pages, assigns each to a signer, saves, then sends.
//
// Uses pdfjs-dist to render each page onto a canvas; fields are absolutely
// positioned as <div> overlays. Coordinates stored NORMALISED (0-1) so they
// survive different render scales (backend uses same normalisation).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as pdfjsLib from 'pdfjs-dist';
import {
  X, PenLine, Type, Calendar, User as UserIcon, CheckSquare, TextCursor,
  Trash2, Loader2, Send, Save, Users, ChevronLeft,
} from 'lucide-react';

// pdfjs worker — served from /public so it's always the exact same version as the installed lib
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = '#0057FF';

// Field types (icon + label + default size in normalised page units)
export const FIELD_TYPES = [
  { type: 'signature', label: 'Signature', icon: PenLine,     defaultW: 0.24, defaultH: 0.055 },
  { type: 'initials',  label: 'Initials',  icon: PenLine,     defaultW: 0.10, defaultH: 0.045 },
  { type: 'date',      label: 'Date',      icon: Calendar,    defaultW: 0.18, defaultH: 0.030 },
  { type: 'name',      label: 'Full name', icon: UserIcon,    defaultW: 0.24, defaultH: 0.030 },
  { type: 'text',      label: 'Text',      icon: TextCursor,  defaultW: 0.24, defaultH: 0.030 },
  { type: 'checkbox',  label: 'Checkbox',  icon: CheckSquare, defaultW: 0.030, defaultH: 0.030 },
];

// Distinct colours per signer index (colour-coded fields)
export const SIGNER_COLOURS = [
  { name: 'Blue',    hex: '#0057FF', tint: '#DBEAFE' },
  { name: 'Emerald', hex: '#059669', tint: '#D1FAE5' },
  { name: 'Amber',   hex: '#D97706', tint: '#FEF3C7' },
  { name: 'Rose',    hex: '#E11D48', tint: '#FFE4E6' },
  { name: 'Violet',  hex: '#7C3AED', tint: '#EDE9FE' },
  { name: 'Cyan',    hex: '#0891B2', tint: '#CFFAFE' },
];

export const colorForSigner = (signers, signerId) => {
  const idx = signers.findIndex((s) => s.id === signerId);
  return SIGNER_COLOURS[Math.max(0, idx) % SIGNER_COLOURS.length];
};

export default function EsignFieldEditor({ doc, authHeaders, onDone, onClose }) {
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pages, setPages] = useState([]); // [{ width, height, viewport }]
  const [fields, setFields] = useState(doc.fields || []);
  const [activeSigner, setActiveSigner] = useState(doc.signers?.[0]?.id);
  const [activeType, setActiveType] = useState('signature');
  const [busy, setBusy] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [savedSig, setSavedSig] = useState(null); // broker's saved signature data URL
  const containerRef = useRef(null);
  const documents = useMemo(() => doc.documents?.length ? doc.documents : [{ id: 'primary', name: doc.title }], [doc.documents, doc.title]);
  const [activeDocId, setActiveDocId] = useState(documents[0].id);
  const docFields = fields.filter((f) => (f.document_id || documents[0].id) === activeDocId);

  // Set of signer ids that are internal brokers (auto-signed with the saved signature)
  const brokerSignerIds = useMemo(
    () => new Set((doc.signers || []).filter((s) => s.is_broker).map((s) => s.id)),
    [doc.signers],
  );

  // Fetch the broker's saved signature so broker-assigned fields preview it
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/esign/my-signature`, { headers: authHeaders })
      .then((r) => { if (!cancelled && r.data?.has_signature) setSavedSig(r.data.signature); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [authHeaders]);

  // Fetch the PDF once, then render each page onto its canvas
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setPages([]); setPdfDoc(null);
        const r = await axios.get(`${API}/api/esign/documents/${doc.id}/file`,
                                  { headers: authHeaders, responseType: 'blob', params: { document_id: activeDocId } });
        if (cancelled) return;
        const blob = new Blob([r.data], { type: 'application/pdf' });
        setPdfBlob(blob);
        const arrBuf = await blob.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrBuf }).promise;
        if (cancelled) return;
        setPdfDoc(pdf);
        const pgs = [];
        for (let i = 1; i <= pdf.numPages; i += 1) {
          const p = await pdf.getPage(i);
          const viewport = p.getViewport({ scale: 1.5 });
          pgs.push({ page: p, width: viewport.width, height: viewport.height });
        }
        setPages(pgs);
      } catch (e) {
        toast.error('Failed to load PDF');
      }
    })();
    return () => { cancelled = true; };
  }, [doc.id, authHeaders, activeDocId]);

  // Render pages into their canvases when `pages` is ready
  useEffect(() => {
    pages.forEach((p, idx) => {
      const canvasEl = document.getElementById(`pdf-canvas-${activeDocId}-${idx + 1}`);
      if (!canvasEl || canvasEl.dataset.rendered === 'yes') return;
      const viewport = p.page.getViewport({ scale: 1.5 });
      canvasEl.width = viewport.width;
      canvasEl.height = viewport.height;
      const ctx = canvasEl.getContext('2d');
      p.page.render({ canvasContext: ctx, viewport }).promise.then(() => {
        canvasEl.dataset.rendered = 'yes';
      });
    });
  }, [pages, activeDocId]);

  // Click on a page to drop a new field at cursor (uses currently-selected type + signer)
  const handlePageClick = useCallback((e, pageIdx) => {
    if (!activeSigner || !activeType) return;
    const wrapper = e.currentTarget;
    const rect = wrapper.getBoundingClientRect();
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top) / rect.height;
    const cfg = FIELD_TYPES.find((f) => f.type === activeType) || FIELD_TYPES[0];
    const nf = {
      id: `tmp-${Math.random().toString(36).slice(2)}`,
      type: activeType,
      page: pageIdx + 1,
      x: Math.max(0, Math.min(1 - cfg.defaultW, nx - cfg.defaultW / 2)),
      y: Math.max(0, Math.min(1 - cfg.defaultH, ny - cfg.defaultH / 2)),
      w: cfg.defaultW,
      h: cfg.defaultH,
      signer_id: activeSigner,
      required: true,
      label: cfg.label,
      document_id: activeDocId,
    };
    setFields((prev) => [...prev, nf]);
    setSelectedFieldId(nf.id);
  }, [activeSigner, activeType, activeDocId]);

  const removeField = (id) => setFields((prev) => prev.filter((f) => f.id !== id));

  // Drag existing field around its page (mouse only for MVP; supports touch via passive listeners implicitly)
  const startFieldDrag = (e, f) => {
    e.stopPropagation();
    setSelectedFieldId(f.id);
    const wrapper = e.currentTarget.parentElement;
    const rect = wrapper.getBoundingClientRect();
    const startX = e.clientX, startY = e.clientY;
    const originX = f.x, originY = f.y;
    const move = (mv) => {
      const dx = (mv.clientX - startX) / rect.width;
      const dy = (mv.clientY - startY) / rect.height;
      setFields((prev) => prev.map((fx) => fx.id === f.id ? {
        ...fx,
        x: Math.max(0, Math.min(1 - fx.w, originX + dx)),
        y: Math.max(0, Math.min(1 - fx.h, originY + dy)),
      } : fx));
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  // Save fields to backend
  const save = async () => {
    setBusy(true);
    try {
      const cleanFields = fields.map((f) => ({
        // strip temp ids so backend regenerates them
        ...(f.id && !f.id.startsWith('tmp-') ? { id: f.id } : {}),
        type: f.type, page: f.page,
        x: f.x, y: f.y, w: f.w, h: f.h,
        signer_id: f.signer_id, required: f.required, label: f.label, document_id: f.document_id || documents[0].id,
      }));
      await axios.put(`${API}/api/esign/documents/${doc.id}/fields`,
                      { fields: cleanFields }, { headers: authHeaders });
      toast.success(`Saved ${cleanFields.length} field${cleanFields.length === 1 ? '' : 's'}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save fields');
    } finally { setBusy(false); }
  };

  const saveAndSend = async () => {
    setBusy(true);
    try {
      const cleanFields = fields.map((f) => ({
        ...(f.id && !f.id.startsWith('tmp-') ? { id: f.id } : {}),
        type: f.type, page: f.page,
        x: f.x, y: f.y, w: f.w, h: f.h,
        signer_id: f.signer_id, required: f.required, label: f.label, document_id: f.document_id || documents[0].id,
      }));
      await axios.put(`${API}/api/esign/documents/${doc.id}/fields`,
                      { fields: cleanFields }, { headers: authHeaders });
      const r = await axios.post(`${API}/api/esign/documents/${doc.id}/send`, { base_url: window.location.origin },
                                  { headers: authHeaders });
      toast.success('Document sent — signing links copied to the drawer');
      onDone?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally { setBusy(false); }
  };

  const signerColour = useMemo(() => {
    const map = {};
    (doc.signers || []).forEach((s, i) => {
      map[s.id] = SIGNER_COLOURS[i % SIGNER_COLOURS.length];
    });
    return map;
  }, [doc.signers]);

  const fieldsForActiveSigner = fields.filter((f) => f.signer_id === activeSigner).length;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between bg-white border-b border-slate-200 px-4 sm:px-6 py-3 shadow-sm">
        <div className="flex items-center gap-3 min-w-0">
          <button type="button" onClick={onClose} data-testid="fields-close"
                  className="p-2 rounded-lg hover:bg-slate-100">
            <ChevronLeft className="w-4 h-4 text-slate-600" />
          </button>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Prepare document</p>
            <p className="text-sm font-extrabold text-slate-950 truncate">{doc.title}</p>
          </div>
          {documents.length > 1 && (
            <div className="hidden sm:flex items-center gap-1 ml-3 rounded-full bg-slate-100 p-0.5" data-testid="fields-doc-tabs">
              {documents.map((d, i) => { const n = fields.filter((f) => (f.document_id || documents[0].id) === d.id).length; return (
                <button key={d.id} type="button" onClick={() => { setActiveDocId(d.id); setSelectedFieldId(null); }} data-testid={`fields-doc-tab-${d.id}`}
                        className={`rounded-full px-3 py-1 text-[11px] font-bold truncate max-w-[180px] ${activeDocId === d.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
                  {i + 1}. {d.name} <span className="text-slate-400">· {n}</span>
                </button>); })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={save} disabled={busy} data-testid="fields-save"
                  className="inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-4 py-2 bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save draft
          </button>
          <button type="button" onClick={saveAndSend} disabled={busy || fields.length === 0} data-testid="fields-save-send"
                  className="inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-4 py-2 text-white shadow-sm hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: BRAND }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Save & Send
          </button>
        </div>
      </div>

      {/* Layout: sidebar + PDF viewport */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 sm:w-72 border-r border-slate-200 bg-white overflow-y-auto p-4 space-y-5">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
              <Users className="w-3 h-3" /> Assign to signer
            </p>
            <div className="space-y-1.5">
              {doc.signers?.map((s) => {
                const c = signerColour[s.id];
                const isActive = s.id === activeSigner;
                const count = fields.filter((f) => f.signer_id === s.id).length;
                return (
                  <button key={s.id} type="button" onClick={() => setActiveSigner(s.id)}
                          data-testid={`fields-signer-${s.id}`}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left transition-all ${
                            isActive ? 'ring-2' : 'hover:bg-slate-50'
                          }`}
                          style={{
                            backgroundColor: isActive ? c.tint : '#fff',
                            borderColor: isActive ? c.hex : '#E2E8F0',
                            border: '1px solid',
                            ...(isActive ? { boxShadow: `0 0 0 2px ${c.hex}30` } : {}),
                          }}>
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: c.hex }} />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-slate-900 truncate">{s.name}</p>
                      <p className="text-[10px] text-slate-500 truncate">{s.role} · {count} field{count === 1 ? '' : 's'}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Field palette</p>
            <p className="text-[10px] text-slate-500 mb-2 leading-relaxed">
              Select a type, then click on the document where you want it placed.
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {FIELD_TYPES.map((ft) => {
                const isActive = ft.type === activeType;
                return (
                  <button key={ft.type} type="button" onClick={() => setActiveType(ft.type)}
                          data-testid={`fields-palette-${ft.type}`}
                          className={`flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl border transition-all ${
                            isActive ? 'ring-2' : 'hover:bg-slate-50 border-slate-200 bg-white'
                          }`}
                          style={{
                            borderColor: isActive ? BRAND : '#E2E8F0',
                            backgroundColor: isActive ? '#DBEAFE' : '#fff',
                            ...(isActive ? { boxShadow: `0 0 0 2px ${BRAND}30` } : {}),
                          }}>
                    <ft.icon className="w-4 h-4" style={{ color: isActive ? BRAND : '#475569' }} />
                    <span className="text-[10px] font-bold text-slate-700">{ft.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-600 leading-relaxed">
            <p className="font-bold text-slate-900 mb-1">Tip</p>
            You can click and drag placed fields to move them. Click a field, press <span className="font-mono bg-white border border-slate-200 px-1 rounded">Del</span> to remove.
            <p className="mt-2 text-slate-500">{fieldsForActiveSigner} field{fieldsForActiveSigner === 1 ? '' : 's'} placed for the active signer.</p>
          </div>
        </aside>

        {/* PDF pages */}
        <div ref={containerRef} className="flex-1 overflow-auto bg-slate-100 p-6"
             onKeyDown={(e) => {
               if ((e.key === 'Delete' || e.key === 'Backspace') && selectedFieldId) {
                 removeField(selectedFieldId); setSelectedFieldId(null);
               }
             }}
             tabIndex={0}>
          {!pdfDoc && (
            <div className="text-center py-16 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin inline-block" /> Loading document…
            </div>
          )}
          {documents.length > 1 && (
            <div className="sm:hidden mb-3 flex gap-1 overflow-x-auto">
              {documents.map((d, i) => <button key={d.id} type="button" onClick={() => setActiveDocId(d.id)} className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-bold ${activeDocId === d.id ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>{i + 1}. {d.name}</button>)}
            </div>
          )}
          {pages.map((p, idx) => {
            const pageFields = docFields.filter((f) => f.page === idx + 1);
            return (
              <div key={`${activeDocId}-${idx}`} className="mx-auto mb-6" style={{ maxWidth: p.width }}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                  {documents.length > 1 ? `${documents.find((d) => d.id === activeDocId)?.name} · ` : ''}Page {idx + 1} of {pages.length}
                </p>
                <div className="relative shadow-lg rounded-lg overflow-hidden bg-white cursor-crosshair"
                     onClick={(e) => handlePageClick(e, idx)}
                     data-testid={`fields-page-${idx + 1}`}>
                  <canvas id={`pdf-canvas-${activeDocId}-${idx + 1}`} className="block" />
                  {/* Field overlays */}
                  {pageFields.map((f) => {
                    const c = signerColour[f.signer_id] || SIGNER_COLOURS[0];
                    const IconComp = FIELD_TYPES.find((x) => x.type === f.type)?.icon || Type;
                    const isSelected = f.id === selectedFieldId;
                    const showSavedSig = savedSig
                      && brokerSignerIds.has(f.signer_id)
                      && (f.type === 'signature' || f.type === 'initials');
                    return (
                      <div key={f.id}
                           className={`absolute flex items-center gap-1 rounded overflow-hidden cursor-move select-none ${isSelected ? 'ring-2 ring-offset-1' : ''}`}
                           data-testid={`fields-placed-${f.id}`}
                           style={{
                             left: `${f.x * 100}%`, top: `${f.y * 100}%`,
                             width: `${f.w * 100}%`, height: `${f.h * 100}%`,
                             backgroundColor: showSavedSig ? '#FFFFFF' : `${c.hex}22`,
                             border: `1.5px solid ${c.hex}`,
                             ...(isSelected ? { boxShadow: `0 0 0 2px ${c.hex}66` } : {}),
                           }}
                           onMouseDown={(e) => startFieldDrag(e, f)}
                           onClick={(e) => { e.stopPropagation(); setSelectedFieldId(f.id); }}>
                        {showSavedSig ? (
                          <>
                            <img src={savedSig} alt="Signature"
                                 data-testid={`fields-saved-sig-${f.id}`}
                                 className="w-full h-full object-contain pointer-events-none p-0.5"
                                 draggable={false} />
                            {isSelected && (
                              <button type="button" onClick={(e) => { e.stopPropagation(); removeField(f.id); }}
                                      className="absolute top-0 right-0 w-4 h-4 rounded-full flex items-center justify-center"
                                      style={{ backgroundColor: c.hex, color: '#fff' }}
                                      data-testid={`fields-remove-${f.id}`}>
                                <X className="w-2.5 h-2.5" />
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            <IconComp className="w-3 h-3 flex-shrink-0 ml-1" style={{ color: c.hex }} />
                            <span className="text-[9px] font-bold uppercase tracking-wider truncate" style={{ color: c.hex }}>
                              {f.label || f.type}
                            </span>
                            {isSelected && (
                              <button type="button" onClick={(e) => { e.stopPropagation(); removeField(f.id); }}
                                      className="ml-auto mr-0.5 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                                      style={{ backgroundColor: c.hex, color: '#fff' }}
                                      data-testid={`fields-remove-${f.id}`}>
                                <X className="w-2.5 h-2.5" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
